/**
 * YearView Component - Standard Calendar Grid
 * 12-month calendar in 4x3 grid with colored days
 * Uses yearly summary API for performance
 */

import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Spin, Empty, Tooltip } from 'antd';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { useYearlySummary } from '@/hooks/useCalendars';
import type { DaySummary, Resource } from '@/api/types/calendars.types';
import './YearView.css';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

interface YearViewProps {
  /** Current year to display */
  year: number;
  /** Calendar ID */
  calendarId: string;
  /** Selected resource IDs for filtering */
  selectedResourceIds?: string[];
  /** All resources (for color mapping) */
  allResources?: Resource[];
  /** Callback when a day is clicked */
  onDayClick?: (date: Date) => void;
  /** Callback when a month is clicked */
  onMonthClick?: (date: Date) => void;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * Get airplane resources from a day summary
 */
const getAirplaneIds = (
  daySummary: DaySummary,
  allResources: Resource[]
): string[] => {
  if (!daySummary || !daySummary.resource_ids) return [];

  return daySummary.resource_ids.filter((id) =>
    allResources.find((r) => r.id === id && r.resource_type === 'AIRPLANE')
  );
};

/**
 * Get background color for a day with events
 * Uses airplane colors or default blue
 */
const getDayBackground = (
  daySummary: DaySummary | undefined,
  allResources: Resource[]
): string => {
  if (!daySummary || daySummary.nr_events === 0) {
    return 'transparent';
  }

  const airplaneIds = getAirplaneIds(daySummary, allResources);

  if (airplaneIds.length === 0) {
    // No specific airplanes, use default blue
    return 'var(--year-event-medium)';
  }

  if (airplaneIds.length === 1) {
    // Single airplane - use its color with opacity
    const airplane = allResources.find((r) => r.id === airplaneIds[0]);
    const color = airplane?.metadata?.color || '#1890ff';
    // Convert hex to rgba with opacity
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.6)`;
  }

  // Multiple airplanes - use gradient or mixed indicator
  if (airplaneIds.length === 2) {
    const colors = airplaneIds.map((id) => {
      const airplane = allResources.find((r) => r.id === id);
      return airplane?.metadata?.color || '#1890ff';
    });
    return `linear-gradient(135deg, ${colors[0]}99 50%, ${colors[1]}99 50%)`;
  }

  // Many airplanes - use striped pattern
  return 'repeating-linear-gradient(45deg, #ffa94066, #ffa94066 2px, #ff7a4566 2px, #ff7a4566 4px)';
};

/**
 * Mini Month Component
 */
interface MiniMonthProps {
  year: number;
  month: number; // 0-11
  summaryDays: Record<string, DaySummary>;
  allResources: Resource[];
  onDayClick?: (date: Date) => void;
  onMonthClick?: (date: Date) => void;
}

const MiniMonth: React.FC<MiniMonthProps> = ({
  year,
  month,
  summaryDays,
  allResources,
  onDayClick,
  onMonthClick,
}) => {
  const { timezone } = useUserTimezone();

  // Generate calendar grid for this month
  const calendarGrid = useMemo(() => {
    const firstDay = dayjs.tz(
      `${year}-${String(month + 1).padStart(2, '0')}-01`,
      timezone
    );
    const startOfMonth = firstDay.startOf('month');
    const endOfMonth = firstDay.endOf('month');

    // Start from previous Sunday
    const startDate = startOfMonth.startOf('week');
    const endDate = endOfMonth.endOf('week');

    const days: Dayjs[] = [];
    let currentDay = startDate;

    while (currentDay.isBefore(endDate) || currentDay.isSame(endDate, 'day')) {
      days.push(currentDay);
      currentDay = currentDay.add(1, 'day');
    }

    return days;
  }, [year, month, timezone]);

  const today = dayjs.tz(new Date(), timezone);

  return (
    <div className="year-mini-month">
      <div
        className="year-month-header"
        onClick={() => {
          if (onMonthClick) {
            onMonthClick(new Date(year, month, 1));
          }
        }}
      >
        {MONTH_NAMES[month]}
      </div>

      <div className="year-month-weekdays">
        {WEEKDAY_LABELS.map((label, i) => (
          <div key={i} className="year-weekday-label">
            {label}
          </div>
        ))}
      </div>

      <div className="year-month-days">
        {calendarGrid.map((day) => {
          const dateKey = day.format('YYYY-MM-DD');
          const daySummary = summaryDays[dateKey];
          const isCurrentMonth = day.month() === month;
          const isToday = day.isSame(today, 'day');
          const hasEvents = daySummary && daySummary.nr_events > 0;

          // Build tooltip content
          let tooltipContent = null;
          if (hasEvents) {
            const airplaneIds = getAirplaneIds(daySummary, allResources);
            const airplanes = airplaneIds
              .map((id) => allResources.find((r) => r.id === id))
              .filter(Boolean);

            tooltipContent = (
              <div style={{ minWidth: 150 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  {day.format('dddd, D. MMMM YYYY')}
                </div>
                <div>Events: {daySummary.nr_events}</div>
                {airplanes.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>Airplanes:</div>
                    {airplanes.map((airplane) => (
                      <div
                        key={airplane!.id}
                        style={{
                          fontSize: 11,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: airplane!.metadata?.color || '#1890ff',
                          }}
                        />
                        {airplane!.resource_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          const dayClasses = [
            'year-day',
            !isCurrentMonth && 'year-day-other-month',
            isToday && 'year-day-today',
            hasEvents && 'year-day-has-events',
          ]
            .filter(Boolean)
            .join(' ');

          const dayStyle: React.CSSProperties = {
            background: isCurrentMonth
              ? getDayBackground(daySummary, allResources)
              : 'transparent',
            cursor: hasEvents && isCurrentMonth ? 'pointer' : 'default',
          };

          const dayElement = (
            <div
              key={dateKey}
              className={dayClasses}
              style={dayStyle}
              onClick={() => {
                if (hasEvents && isCurrentMonth && onDayClick) {
                  onDayClick(day.toDate());
                }
              }}
            >
              {day.date()}
            </div>
          );

          return tooltipContent ? (
            <Tooltip key={dateKey} title={tooltipContent} placement="top">
              {dayElement}
            </Tooltip>
          ) : (
            dayElement
          );
        })}
      </div>
    </div>
  );
};

/**
 * Main YearView Component
 */
export const YearView: React.FC<YearViewProps> = ({
  year,
  calendarId,
  selectedResourceIds,
  allResources = [],
  onDayClick,
  onMonthClick,
}) => {
  // Use optimized yearly summary hook
  const {
    data: summary,
    isLoading,
    error,
  } = useYearlySummary(calendarId, year, selectedResourceIds, true);

  if (isLoading) {
    return (
      <div className="year-view-loading">
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading {year} calendar...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="year-view-empty">
        <Empty description="Failed to load calendar" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="year-view-empty">
        <Empty description="No data available" />
      </div>
    );
  }

  return (
    <div className="year-view">
      <div className="year-view-grid">
        {Array.from({ length: 12 }, (_, i) => (
          <MiniMonth
            key={i}
            year={year}
            month={i}
            summaryDays={summary.days}
            allResources={allResources}
            onDayClick={onDayClick}
            onMonthClick={onMonthClick}
          />
        ))}
      </div>
    </div>
  );
};
